import { createContext, useState, useContext, useEffect } from "react";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { BaseUrlApi, ErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { filter } from "motion/react-client";

const NotificationContext = createContext();

export const useNotification = () => {
  return useContext(NotificationContext);
};

export const NotificationProvider = ({ children }) => {
  const { user, token } = useAuth();
  const userId = user?.id;
  const [loadingSend, setLoadingSend] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const getAllNotifications = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/notifications`);
      setNotifications(data.data);
    } catch (error) {
      toast("Error", {
        description: ErrorMessage(error),
      });
    } finally {
      setLoadingNotifications(false);
    }
  };

  const markAsRead = async (id) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id)
    );

    try {
      await axios.put(`${BaseUrlApi}/notifications/${id}`, {
        is_read: true,
      });
      getAllNotifications();
      toast.success("Notification marked as read");
    } catch (error) {
      toast("Error", {
        description: ErrorMessage(error),
      });
    }
  };

  const sendNotification = async (e, title, message) => {
    e.preventDefault();
    try {
      setLoadingSend(true);
      await axios.post(`${BaseUrlApi}/notifications`, {
        user_id: userId,
        title,
        message,
      });
      toast.success("Notification sent successfully!");
    } catch (error) {
      toast.error("Failed to send notification");
    } finally {
      setLoadingSend(false);
    }
  };

  window.Pusher = Pusher;
  const echo = new Echo({
    broadcaster: "reverb",
    key: process.env.REACT_APP_REVERB_APP_KEY,
    wsHost: process.env.REACT_APP_REVERB_HOST,
    wsPort: process.env.REACT_APP_REVERB_PORT ?? 80,
    wssPort: process.env.REACT_APP_REVERB_PORT ?? 443,
    forceTLS: (process.env.REACT_APP_REVERB_SCHEME ?? "https") === "https",
    enabledTransports: ["ws", "wss"],
    authEndpoint: process.env.REACT_APP_REVERB_AUTH_ENDPOINT,
  });

  useEffect(() => {
    if (token) {
      // Listen for real-time notifications
      echo
        .private(`user.${userId}`)
        .listen(".notification.created", (event) => {
          setNotifications((prev) => [event, ...prev]);
          console.log(event);
        });

      getAllNotifications();

      return () => {
        echo.leave(`user.${userId}`);
      };
    }
  }, [userId, token]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        sendNotification,
        loadingSend,
        getAllNotifications,
        loadingNotifications,
        markAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
